import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';

// item: "stay inside the project" vs ala-carte (Basix Six). When you open a project
// we remember it; the project context bar + breadcrumbs then persist as you click
// into its proposals / work orders / invoices (those are GLOBAL routes that don't
// carry the project in the URL). Landing on any Basix Six list / the dashboard
// clears it, dropping you back to ala-carte mode.

export interface ActiveProject {
  id: string;
  name: string;
}

interface ProjectModeContextValue {
  activeProject: ActiveProject | null;
  enterProject: (p: ActiveProject) => void;
  exitProject: () => void;
}

const ProjectModeContext = createContext<ProjectModeContextValue>({
  activeProject: null,
  enterProject: () => {},
  exitProject: () => {},
});

export const useProjectMode = (): ProjectModeContextValue => useContext(ProjectModeContext);

// top-level "ala carte" destinations — arriving here exits project mode
const ALA_CARTE = new Set([
  '/', '/proposals', '/workorders', '/invoices', '/bills', '/purchaseorder',
  '/customers', '/items', '/reports', '/users', '/settings', '/appearance', '/search',
]);

const STORAGE_KEY = 'basix:activeProject';

export const ProjectModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeProject, setActiveProject] = useState<ActiveProject | null>(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as ActiveProject) : null;
    } catch {
      return null;
    }
  });
  const { pathname } = useLocation();

  const enterProject = useCallback((p: ActiveProject) => {
    setActiveProject((prev) => (prev?.id === p.id && prev?.name === p.name ? prev : p));
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
  }, []);

  const exitProject = useCallback(() => {
    setActiveProject(null);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  // arriving on a Basix Six list / dashboard leaves project mode
  useEffect(() => {
    if (ALA_CARTE.has(pathname.toLowerCase())) exitProject();
  }, [pathname, exitProject]);

  const value = useMemo(
    () => ({ activeProject, enterProject, exitProject }),
    [activeProject, enterProject, exitProject],
  );

  return <ProjectModeContext.Provider value={value}>{children}</ProjectModeContext.Provider>;
};

export default ProjectModeProvider;
